import React, { useEffect, memo, useState, useRef } from 'react';
import './index.less';
import { ConfigProvider, Tabs, TabsProps, Modal, App } from 'antd';
import VideoList from './Video';
import { IInitInfo } from '../..';
import { asyncStudyProgress, getPredictScoreExamPointRecommendResource } from '@/apis';
import SubPlayer from '@/components/video/sub-player';
import queryDomMethods from '@/utils/queryDomMethods';
import { IPredictionScoreExamPointRecommendResource } from '@/types';

type IList = IPredictionScoreExamPointRecommendResource['list'];

interface IProps {
    initInfo?: IInitInfo;
}

const ExamPointVideos = ({ initInfo }: IProps) => {
    const { message } = App.useApp();
    const [items, setItems] = useState<TabsProps['items']>();
    const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
    const [videoInfo, setVideoInfo] = useState<IList[number]>();
    const [studentId, setStudentId] = useState<number>();

    const fetchSyncProgress = ({ resourceId, csItemId, courseId, progress }: IList[number]) => {
        // 只上报课程内视频的进度
        if (resourceId && csItemId && courseId) {
            asyncStudyProgress({
                resource_id: resourceId,
                cs_item_id: csItemId,
                course_id: courseId,
                status: progress === 0 ? 2 : progress,
            });
        }
    };

    const handleOpenVideoModal = (info: IList[number]) => {
        if (info.discriminator === 'video') {
            if (!info.videoId) {
                message.error('视频资源不存在');
                return;
            }
            setIsPlayerModalOpen(true);
            setVideoInfo(info);
            fetchSyncProgress(info);
            return;
        }
        if (info.discriminator === 'live_new') {
            if (info.liveResource.simplifyRecordUrl) {
                setIsPlayerModalOpen(true);
                setVideoInfo({
                    ...info,
                    videoId: info.liveResource.simplifyRecordUrl,
                });
                fetchSyncProgress(info);
                return;
            }
            if (info.liveResource.urlLivePlayBackPc) {
                fetchSyncProgress(info);
                window.open(info.liveResource.urlLivePlayBackPc, '_blank');
                return;
            }
            message.error('视频资源不存在');
            return;
        }
    };

    const createCourseItem = (
        { courseId, courseName }: { courseId: number; courseName: string },
        isCurrentCourse: boolean,
        list: IList,
        currentPage: number,
        totalCount: number,
        pageSize: number
    ) => {
        const defaultList = isCurrentCourse ? list : [];
        const defaultPagination = isCurrentCourse ? { currentPage, totalCount, pageSize } : undefined;
        return {
            key: `${courseId}`,
            label: courseName,
            children: (
                <VideoList
                    initInfo={initInfo!}
                    id={{ courseId }}
                    defaultList={defaultList}
                    defaultPagination={defaultPagination}
                    handleOpenVideoModal={handleOpenVideoModal}
                />
            ),
        };
    };

    const createKnowledgeItem = (
        { knowledgeId, knowledgeName }: { knowledgeId: number; knowledgeName: string },
        isCurrentKnowledge: boolean,
        list: IList,
        currentPage: number,
        totalCount: number,
        pageSize: number
    ) => {
        const defaultList = isCurrentKnowledge ? list : [];
        const defaultPagination = isCurrentKnowledge ? { currentPage, totalCount, pageSize } : undefined;

        return {
            key: `${knowledgeId}`,
            label: knowledgeName,
            children: (
                <VideoList
                    initInfo={initInfo!}
                    id={{ knowledgeId }}
                    defaultList={defaultList}
                    defaultPagination={defaultPagination}
                    handleOpenVideoModal={handleOpenVideoModal}
                />
            ),
        };
    };

    const fetchRecommendResource = async () => {
        const { knowledgeId, projectId, subjectId, examSeasonId } = initInfo!;
        const response = await getPredictScoreExamPointRecommendResource({
            examSeasonId,
            knowledgeId,
            projectId,
            subjectId,
            queryType: 0, // 查询类型 0 首次进入，1 查询指定课程(courseId不能为空) 2 查询考点
            pageNum: 1,
            pageSize: 8,
        });
        if (response.status === 0 && response.result) {
            const { courseList, currentCourseId, list, currentPage, totalCount, pageSize, knowledgeRecommendResources, studentId } =
                response.result;
            setStudentId(studentId);
            const items =
                courseList?.map(course =>
                    createCourseItem(course, course.courseId === currentCourseId, list, currentPage, totalCount, pageSize)
                ) || [];
            if (knowledgeRecommendResources?.knowledgeId) {
                items.push(createKnowledgeItem(knowledgeRecommendResources, !currentCourseId, list, currentPage, totalCount, pageSize));
            }
            setItems(items);
        }
    };

    useEffect(() => {
        initInfo?.hasAllRequiredParams && fetchRecommendResource();
    }, [initInfo?.knowledgeId]);

    return items?.length ? (
        <div className="sub-tiku__exam-point-videos">
            <ConfigProvider
                theme={{
                    components: {
                        Tabs: {
                            itemColor: '#666666',
                            itemActiveColor: '#333333',
                            itemHoverColor: '#333333',
                            itemSelectedColor: '#333333',
                        },
                    },
                }}
            >
                <Tabs size="small" items={items} indicator={{ size: 24, align: 'center' }} />
            </ConfigProvider>
            <Modal
                wrapClassName="sub-tiku__exam-point-video-modal"
                width={900}
                footer={null}
                closeIcon={false}
                open={isPlayerModalOpen}
                onCancel={() => setIsPlayerModalOpen(false)}
                destroyOnClose
                styles={{ content: { padding: '0px' }, body: { height: '600px' } }}
                getContainer={queryDomMethods.querySubTikuRoot}
            >
                {videoInfo?.videoId && (
                    <SubPlayer
                        videoId={videoInfo.videoId}
                        isTrack={!!videoInfo.courseId}
                        trackApiParams={
                            videoInfo.courseId
                                ? {
                                      courseId: videoInfo.courseId,
                                      resourceId: videoInfo.resourceId,
                                  }
                                : undefined
                        }
                        studentId={studentId}
                        businessNote={
                            videoInfo.courseId
                                ? {
                                      courseId: videoInfo.courseId,
                                      csItemId: videoInfo.csItemId,
                                      resourceId: videoInfo.resourceId,
                                      liveId: videoInfo.liveResource?.liveId || undefined,
                                      resourceType: videoInfo.discriminator,
                                  }
                                : undefined
                        }
                    />
                )}
            </Modal>
        </div>
    ) : null;
};

export default memo(ExamPointVideos);
